import {
  ArrowsClockwiseIcon,
  ClockCounterClockwiseIcon,
  CrownSimpleIcon,
  DownloadSimpleIcon,
  GiftIcon,
  StarIcon,
} from "@phosphor-icons/react";
import { cn } from "@reactive-resume/utils";
import { useState } from "react";

import { DashboardContent } from "../../components/layout/dashboard-content";
import { useLoyaltyDashboard } from "./hooks/use-loyalty-dashboard";

const TRANSACTION_LABELS: Record<string, string> = {
  PURCHASE: "Achat d’une offre",
  CREATE_RESUME: "Création d’un CV",
  COMPLETE_PROFILE: "Profil complété",
  REFERRAL_ACTIVATED: "Filleul activé",
  REVIEW_VERIFIED: "Avis validé",
  REDEMPTION: "Récompense utilisée",
  RESUME_DOWNLOADED: "Téléchargement d’un CV",
};

const REWARD_LABELS: Record<
  string,
  {
    title: string;
    description: string;
  }
> = {
  RESUME_CREDIT: {
    title: "Un téléchargement",
    description: "Ajoute un téléchargement autonome à votre compte.",
  },

  COVER_LETTER_CREDITS: {
    title: "Trois lettres de motivation",
    description: "Débloque trois créations de lettres de motivation.",
  },

  SMART_START_PACK: {
    title: "Pack SMART START",
    description: "Recevez gratuitement une offre SMART START.",
  },

  SMART_JOB_PACK: {
    title: "Pack SMART JOB",
    description: "Recevez gratuitement une offre SMART JOB.",
  },

  SMART_PRO_PACK: {
    title: "Pack SMART PRO",
    description: "Recevez gratuitement une offre SMART PRO.",
  },
};

const formatDate = (value: string) => {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Date inconnue";
  }

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const LoadingState = () => {
  return (
    <div role="status" className="space-y-3 motion-safe:animate-pulse">
      <span className="sr-only">Chargement du programme de fidélité…</span>

      <div className="h-28 rounded-2xl bg-slate-200/70" />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <div className="h-44 rounded-2xl bg-slate-200/70" />
        <div className="h-44 rounded-2xl bg-slate-200/70" />
        <div className="h-44 rounded-2xl bg-slate-200/70" />
      </div>

      <div className="h-60 rounded-2xl bg-slate-200/70" />
    </div>
  );
};

export const LoyaltyPage = () => {
  const [page, setPage] = useState(1);

  const { data, loading, refreshing, error, redeeming, refetch, redeem } =
    useLoyaltyDashboard(page);

  const totalPages = data
    ? Math.max(1, Math.ceil(data.pagination.total / data.pagination.pageSize))
    : 1;

  return (
    <DashboardContent>
      <section className="min-w-0 space-y-4">
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-xl font-bold tracking-tight text-indigo-950 sm:text-2xl">
              Points de fidélité
            </h1>

            <p className="mt-1 max-w-2xl text-xs leading-5 text-slate-600 sm:text-[13px]">
              Cumulez des points grâce à vos actions, puis échangez-les contre des avantages.
            </p>
          </div>

          <button
            type="button"
            disabled={refreshing}
            onClick={() => void refetch()}
            className={cn(
              "flex min-h-10 shrink-0 items-center justify-center gap-2",
              "rounded-xl border border-slate-200 bg-white px-4",
              "text-xs font-semibold text-indigo-700 shadow-sm",
              "transition-colors hover:bg-indigo-50",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-indigo-500 disabled:opacity-50",
            )}
          >
            <ArrowsClockwiseIcon
              aria-hidden
              size={16}
              className={refreshing ? "animate-spin" : undefined}
            />

            {refreshing ? "Actualisation…" : "Actualiser"}
          </button>
        </header>

        {loading ? (
          <LoadingState />
        ) : !data ? (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
            <p className="text-sm font-semibold text-rose-800">Impossible de charger vos points</p>

            <p className="mt-1 text-xs leading-5 text-rose-700">
              Vérifiez que le backend est lancé puis réessayez.
            </p>

            <button
              type="button"
              onClick={() => void refetch()}
              className={cn(
                "mt-3 min-h-10 rounded-xl",
                "border border-rose-300 px-4",
                "text-xs font-semibold text-rose-800",
                "transition-colors hover:bg-rose-100",
                "focus-visible:outline-none focus-visible:ring-2",
                "focus-visible:ring-rose-500",
              )}
            >
              Réessayer
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div
                role="alert"
                className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900"
              >
                L’actualisation a échoué. Les dernières données chargées restent affichées.
              </div>
            )}

            <article className="relative overflow-hidden rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-indigo-50 p-5 shadow-sm">
              <div
                aria-hidden
                className="absolute -right-8 -top-10 size-40 rounded-full bg-amber-200/30 blur-2xl"
              />

              <div className="relative flex items-center gap-4">
                <span className="grid size-14 shrink-0 place-items-center rounded-2xl bg-amber-400 text-white shadow-lg shadow-amber-200">
                  <StarIcon aria-hidden size={29} weight="fill" />
                </span>

                <div>
                  <p className="text-xs font-medium text-amber-700">Votre solde disponible</p>

                  <p className="mt-1 text-2xl font-bold text-indigo-950 sm:text-3xl">
                    {data.balance.toLocaleString("fr-FR")} <span className="text-base">points</span>
                  </p>
                </div>
              </div>
            </article>

            <section>
              <div className="mb-3">
                <h2 className="text-base font-bold text-indigo-950 sm:text-lg">
                  Utiliser mes points
                </h2>

                <p className="mt-1 text-[11px] leading-4 text-slate-600 sm:text-xs">
                  Une récompense est attribuée une seule fois par demande validée.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {data.rewards.map((reward) => {
                  const information = REWARD_LABELS[reward.code] ?? {
                    title: reward.code.replace(/_/g, " "),
                    description: "Avantage exclusif du programme de fidélité.",
                  };

                  const affordable = data.balance >= reward.costPoints;

                  const isPack = reward.kind === "plan";

                  return (
                    <article
                      key={reward.code}
                      className="flex min-h-[176px] flex-col rounded-2xl border border-slate-200 bg-white p-4 shadow-sm"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <span
                          className={cn(
                            "grid size-10 shrink-0 place-items-center rounded-xl",
                            isPack
                              ? "bg-indigo-50 text-indigo-700"
                              : "bg-emerald-50 text-emerald-600",
                          )}
                        >
                          {isPack ? (
                            <CrownSimpleIcon aria-hidden size={21} weight="duotone" />
                          ) : (
                            <DownloadSimpleIcon aria-hidden size={21} weight="duotone" />
                          )}
                        </span>

                        <span className="rounded-full bg-amber-50 px-2.5 py-1 text-[10px] font-bold text-amber-700">
                          {reward.costPoints.toLocaleString("fr-FR")} pts
                        </span>
                      </div>

                      <h3 className="mt-3 text-sm font-bold text-indigo-950">
                        {information.title}
                      </h3>

                      <p className="mt-1 flex-1 text-[11px] leading-4 text-slate-600">
                        {information.description}
                      </p>

                      <button
                        type="button"
                        disabled={!affordable || redeeming}
                        onClick={() => void redeem(reward.code)}
                        className={cn(
                          "mt-4 flex min-h-10 w-full items-center justify-center gap-2",
                          "rounded-xl px-4 text-xs font-semibold",
                          "transition-colors focus-visible:outline-none",
                          "focus-visible:ring-2 focus-visible:ring-indigo-500",
                          affordable
                            ? "bg-indigo-700 text-white hover:bg-indigo-800"
                            : "cursor-not-allowed bg-slate-100 text-slate-400",
                        )}
                      >
                        <GiftIcon aria-hidden size={16} />

                        {redeeming
                          ? "Traitement…"
                          : affordable
                            ? "Échanger mes points"
                            : "Points insuffisants"}
                      </button>
                    </article>
                  );
                })}
              </div>
            </section>

            <article className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <header className="flex items-center gap-3 border-b border-slate-200 px-4 py-4 sm:px-5">
                <span className="grid size-9 place-items-center rounded-xl bg-indigo-50 text-indigo-700">
                  <ClockCounterClockwiseIcon aria-hidden size={20} weight="duotone" />
                </span>

                <div>
                  <h2 className="text-sm font-bold text-indigo-950 sm:text-base">
                    Historique des points
                  </h2>

                  <p className="mt-0.5 text-[10px] text-slate-500 sm:text-[11px]">
                    Entrées et utilisations enregistrées sur votre compte.
                  </p>
                </div>
              </header>

              {data.entries.length === 0 ? (
                <div className="grid min-h-48 place-items-center p-6 text-center">
                  <div>
                    <StarIcon
                      aria-hidden
                      size={32}
                      weight="duotone"
                      className="mx-auto text-indigo-500"
                    />

                    <p className="mt-3 text-sm font-semibold text-indigo-950">
                      Aucun mouvement pour le moment
                    </p>

                    <p className="mt-1 text-xs text-slate-500">
                      Vos prochains gains apparaîtront ici.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {data.entries.map((entry) => {
                    const positive = entry.points > 0;

                    return (
                      <div key={entry.id} className="flex items-center gap-3 px-4 py-3.5 sm:px-5">
                        <span
                          className={cn(
                            "grid size-9 shrink-0 place-items-center rounded-full",
                            positive
                              ? "bg-emerald-50 text-emerald-600"
                              : "bg-rose-50 text-rose-600",
                          )}
                        >
                          <StarIcon aria-hidden size={17} weight={positive ? "fill" : "regular"} />
                        </span>

                        <div className="min-w-0 flex-1">
                          <p className="truncate text-xs font-semibold text-indigo-950">
                            {TRANSACTION_LABELS[entry.type] ?? entry.type}
                          </p>

                          <p className="mt-0.5 text-[10px] text-slate-500">
                            {formatDate(entry.createdAt)}
                          </p>
                        </div>

                        <div className="shrink-0 text-right">
                          <p
                            className={cn(
                              "text-xs font-bold",
                              positive ? "text-emerald-600" : "text-rose-600",
                            )}
                          >
                            {positive ? "+" : ""}
                            {entry.points.toLocaleString("fr-FR")} pts
                          </p>

                          <p className="mt-0.5 text-[10px] text-slate-500">
                            Solde : {entry.balanceAfter.toLocaleString("fr-FR")}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </article>

            {totalPages > 1 && (
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  disabled={refreshing || data.pagination.page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                  className={cn(
                    "min-h-10 rounded-xl border",
                    "border-slate-200 bg-white px-4",
                    "text-xs font-semibold text-indigo-700",
                    "transition-colors hover:bg-indigo-50",
                    "disabled:cursor-not-allowed disabled:opacity-40",
                  )}
                >
                  Précédent
                </button>

                <span className="text-xs text-slate-600">
                  Page {data.pagination.page} sur {totalPages}
                </span>

                <button
                  type="button"
                  disabled={refreshing || data.pagination.page >= totalPages}
                  onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
                  className={cn(
                    "min-h-10 rounded-xl border",
                    "border-slate-200 bg-white px-4",
                    "text-xs font-semibold text-indigo-700",
                    "transition-colors hover:bg-indigo-50",
                    "disabled:cursor-not-allowed disabled:opacity-40",
                  )}
                >
                  Suivant
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </DashboardContent>
  );
};
